import { useEffect, useMemo, useState } from "react";
import {
  CUSTOM_LIMITS,
  MAX_TRAINING_VIDEO_MB,
  PICTOS,
  PICTO_NOTE,
  TRAINING_BG_TOPICS,
  bgCoverage,
  type CatalogSection,
  type CustomSection,
  type TrainingAssignment,
  type TrainingCourseInfo,
  type TrainingCustom,
  type TrainingQuestion,
  type TrainingSettings,
  type TrainingSlide,
  type VeroScene,
} from "@handvero/shared";
import { api, errorText, upload } from "../api";
import { Picto } from "./Pictograms";
import { Icon, Message, Sheet } from "./ui";

/*
 * Einrichtung der Schulung durch Inhaber und Admins: welche Module gelten, was die Firma selbst ergänzt,
 * wer die Schulung machen muss und welche Module jede Person bekommt. Die festen Pflichtinhalte lassen sich nicht abschalten.
 */

type Tab = "general" | "modules" | "own" | "people";

const TABS: [Tab, string][] = [
  ["general", "Allgemein"],
  ["modules", "Module"],
  ["own", "Eigene Inhalte"],
  ["people", "Personen"],
];

const SCENES: [VeroScene, string][] = [
  ["hello", "Vero winkt"],
  ["point", "Vero zeigt"],
  ["alarm", "Vero warnt"],
  ["walk", "Vero geht"],
  ["think", "Vero überlegt"],
  ["check", "Vero hakt ab"],
];

const rid = (prefix: string) => `${prefix}${Math.random().toString(36).slice(2, 10).replace(/[^a-z0-9]/g, "x")}`;
const newQuestion = (): TrainingQuestion => ({ id: rid("q-"), text: "", options: ["", ""], correct: [0] });
const newSlide = (): TrainingSlide => ({ title: "", text: "", scene: "point" });

export function SetupSheet({ info, onClose, onSaved }: { info: TrainingCourseInfo; onClose: () => void; onSaved: () => void }) {
  const courseId = info.course.id;
  const [settings, setSettings] = useState<TrainingSettings>(info.settings);
  const [custom, setCustom] = useState<TrainingCustom>(info.settings.custom ?? { sections: [], questions: {} });
  const [people, setPeople] = useState<TrainingAssignment[] | null>(null);
  const [peopleDirty, setPeopleDirty] = useState(false);
  const [tab, setTab] = useState<Tab>("general");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState<Record<string, number>>({});

  useEffect(() => {
    api.get<TrainingAssignment[]>(`/api/trainings/${courseId}/assignments`).then(setPeople, (err) => setError(errorText(err)));
  }, [courseId]);

  const catalog = info.catalog;
  const basis = catalog.filter((s) => s.kind === "basis");
  const optional = catalog.filter((s) => s.kind === "optional");
  const modules = settings.enabledModules;
  const toggleModule = (id: string) =>
    setSettings((s) => ({ ...s, enabledModules: s.enabledModules.includes(id) ? s.enabledModules.filter((x) => x !== id) : [...s.enabledModules, id] }));

  const sectionIds = useMemo(
    () => [...basis.map((s) => s.id), ...optional.filter((s) => modules.includes(s.id)).map((s) => s.id)],
    [basis, optional, modules],
  );
  const hasCompanyInput =
    custom.sections.length > 0 || Object.values(custom.questions).some((l) => l.length) || Object.values(settings.notes).some((n) => n.trim());
  const coverage = useMemo(() => bgCoverage(sectionIds, hasCompanyInput), [sectionIds, hasCompanyInput]);

  async function save() {
    setSaving(true);
    setError("");
    try {
      await api.put(`/api/trainings/${courseId}/settings`, {
        instructor: settings.instructor,
        contact: settings.contact,
        notes: settings.notes,
        validMonths: settings.validMonths,
        mandatory: settings.mandatory,
        enabledModules: settings.enabledModules,
        custom,
      });
      if (peopleDirty && people) {
        await api.put(`/api/trainings/${courseId}/assignments`, { assignments: people.map((p) => ({ userId: p.userId, required: p.required, modules: p.modules })) });
      }
      onSaved();
    } catch (err) {
      setError(errorText(err));
      setSaving(false);
    }
  }

  async function uploadVideo(sectionId: string, file: File) {
    if (file.size > MAX_TRAINING_VIDEO_MB * 1024 * 1024) {
      setError(`Das Video ist zu groß. Erlaubt sind bis zu ${MAX_TRAINING_VIDEO_MB} MB.`);
      return;
    }
    setError("");
    setBusy((b) => ({ ...b, [sectionId]: 0 }));
    try {
      const form = new FormData();
      form.append("video", file, file.name);
      const next = await upload<TrainingSettings>(`/api/trainings/${courseId}/sections/${sectionId}/video`, form, (p) => setBusy((b) => ({ ...b, [sectionId]: p })));
      setSettings((s) => ({ ...s, videos: next.videos }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy((b) => Object.fromEntries(Object.entries(b).filter(([k]) => k !== sectionId)));
    }
  }

  async function removeVideo(sectionId: string) {
    try {
      const next = await api.del<TrainingSettings>(`/api/trainings/${courseId}/sections/${sectionId}/video`);
      setSettings((s) => ({ ...s, videos: next.videos }));
    } catch (err) {
      setError(errorText(err));
    }
  }

  /** Hinweise und eigenes Video je Abschnitt, aufklappbar unter dem Modul. */
  const extras = (id: string, hint: string) => {
    const video = settings.videos.find((v) => v.sectionId === id);
    const progress = busy[id];
    return (
      <details className="trn-mod-more">
        <summary>Hinweise für Ihren Betrieb{video ? " · eigenes Video" : ""}</summary>
        <label>
          Was gilt bei Ihnen?
          <textarea rows={2} maxLength={1500} placeholder={hint} value={settings.notes[id] ?? ""} onChange={(e) => setSettings({ ...settings, notes: { ...settings.notes, [id]: e.target.value } })} />
        </label>
        <div className="trn-setup-video">
          {progress !== undefined ? (
            <span className="muted-s">Video wird hochgeladen … {Math.round(progress * 100)} %</span>
          ) : video ? (
            <>
              <span>
                <Icon name="eye" /> Eigenes Video: {video.name}
              </span>
              <button type="button" className="btn-text danger" onClick={() => void removeVideo(id)}>
                Entfernen
              </button>
            </>
          ) : (
            <>
              <span className="muted-s">Statt Vero-Film ein eigenes Video zeigen</span>
              <label className="btn-text trn-file">
                Video hochladen
                <input
                  type="file"
                  accept="video/mp4,video/webm"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) void uploadVideo(id, f);
                  }}
                />
              </label>
            </>
          )}
        </div>
      </details>
    );
  };

  const moduleCard = (s: CatalogSection | CustomSection, kind: "basis" | "optional" | "custom") => {
    const fixed = kind === "basis";
    const isCatalog = "topics" in s;
    const pictos = isCatalog ? s.pictos : [...new Set(s.slides.flatMap((x) => x.pictos ?? []))];
    const slides = isCatalog ? s.slides : s.slides.length;
    const questions = isCatalog ? s.questions : s.questions.length;
    const on = fixed || modules.includes(s.id);
    return (
      <div key={s.id} className={`trn-mod${on ? " on" : ""}${fixed ? " fixed" : ""}`}>
        <input type="checkbox" checked={on} disabled={fixed} onChange={() => toggleModule(s.id)} aria-label={s.title} />
        <b>{s.title}</b>
        <p>{s.summary}</p>
        <div className="trn-chips">
          <span className={`trn-chip${fixed ? " fixed" : ""}`}>{fixed ? "Pflicht, immer dabei" : kind === "custom" ? "Von Ihnen" : "Zuschaltbar"}</span>
          <span className="trn-chip">{slides} Folien</span>
          <span className="trn-chip">{questions} Fragen</span>
          {pictos.slice(0, 8).map((p) => (
            <Picto key={p} id={p} size={26} label={false} />
          ))}
        </div>
        {on && <div style={{ gridColumn: 2 }}>{extras(s.id, isCatalog ? s.noteHint : "Zum Beispiel: Was gilt zusätzlich in Ihrem Betrieb?")}</div>}
      </div>
    );
  };

  return (
    <Sheet title={`Einrichten: ${info.course.title}`} onClose={onClose}>
      <div className="app-form trn-setup">
        <div className="trn-tabs" role="tablist">
          {TABS.map(([id, label]) => (
            <button key={id} role="tab" aria-selected={tab === id} className={tab === id ? "on" : undefined} onClick={() => setTab(id)} type="button">
              {label}
            </button>
          ))}
        </div>

        {tab === "general" && (
          <>
            <p className="muted-s">
              Ob und wie die Schulung bei Ihnen läuft, legen Sie fest. Die Pflichtthemen der Berufsgenossenschaften sind fest eingebaut. Alles andere schalten Sie nach Ihrem
              Betrieb zu.
            </p>
            <label>
              Verantwortliche Person für Rückfragen
              <input value={settings.instructor} maxLength={120} placeholder="z. B. Jan Müller, Meister" onChange={(e) => setSettings({ ...settings, instructor: e.target.value })} />
            </label>
            <label>
              So ist sie erreichbar
              <input value={settings.contact} maxLength={160} placeholder="z. B. 0371 123456 oder im Büro" onChange={(e) => setSettings({ ...settings, contact: e.target.value })} />
            </label>
            <div className="trn-setup-valid">
              <span>Wiederholen nach</span>
              <div className="choice">
                {([12, 6] as const).map((m) => (
                  <button key={m} type="button" className={settings.validMonths === m ? "on" : undefined} onClick={() => setSettings({ ...settings, validMonths: m })}>
                    {m === 12 ? "12 Monaten" : "6 Monaten (Jugendliche)"}
                  </button>
                ))}
              </div>
            </div>
            <div className="trn-setup-valid">
              <span>Wer muss die Schulung machen?</span>
              <div className="choice">
                <button type="button" className={settings.mandatory === "all" ? "on" : undefined} onClick={() => setSettings({ ...settings, mandatory: "all" })}>
                  Alle Mitarbeiter
                </button>
                <button type="button" className={settings.mandatory === "selected" ? "on" : undefined} onClick={() => setSettings({ ...settings, mandatory: "selected" })}>
                  Nur ausgewählte
                </button>
              </div>
              <small className="muted-s">
                {settings.mandatory === "all" ? "Unter „Personen“ nehmen Sie einzelne aus, die sie nicht brauchen." : "Unter „Personen“ legen Sie fest, wer sie machen muss."}
              </small>
            </div>
            <h4 className="trn-h4">Mindestthemen der Berufsgenossenschaft</h4>
            <p className="muted-s">So deckt Ihre Auswahl die üblichen Mindestthemen der Erstunterweisung ab. Gelbe Punkte kann nur Ihr Betrieb füllen, mit Hinweisen oder eigenen Abschnitten.</p>
            <div className="trn-bg">
              {coverage.map(({ topic, status }) => (
                <div key={topic.id} className={status}>
                  <i />
                  <span>
                    {topic.title}
                    <small>
                      {status === "covered"
                        ? "Abgedeckt"
                        : status === "company-done"
                          ? "Von Ihnen ergänzt"
                          : status === "company-open"
                            ? "Bitte ergänzen Sie hier Ihre Verhältnisse (Hinweise oder eigener Abschnitt)"
                            : `Nicht dabei. Zuschalten: ${topic.sections.map((id) => catalog.find((c) => c.id === id)?.title ?? id).join(", ")}`}
                    </small>
                  </span>
                </div>
              ))}
            </div>
            <small className="muted-s">{TRAINING_BG_TOPICS.length} Themen. Stand der Übersicht: BG-Nachweisbuch, ohne Rechtsberatung.</small>
          </>
        )}

        {tab === "modules" && (
          <>
            <p className="muted-s">Pflichtabschnitte sind immer dabei. Die übrigen Module schalten Sie ein, wenn sie zu Ihren Arbeitsplätzen passen. Wer keine Gefahren durch Strom hat, braucht „Elektrik“ nicht.</p>
            <h4 className="trn-h4">Pflicht</h4>
            {basis.map((s) => moduleCard(s, "basis"))}
            <h4 className="trn-h4">Zuschaltbar</h4>
            {optional.map((s) => moduleCard(s, "optional"))}
            {custom.sections.length > 0 && (
              <>
                <h4 className="trn-h4">Ihre eigenen Abschnitte</h4>
                {custom.sections.map((s) => moduleCard(s, "custom"))}
              </>
            )}
          </>
        )}

        {tab === "own" && (
          <OwnContent
            catalog={catalog}
            custom={custom}
            onChange={(c) => {
              setCustom(c);
              // Neue eigene Abschnitte sind gleich eingeschaltet, gelöschte fallen aus der Auswahl.
              setSettings((s) => {
                const ids = new Set(c.sections.map((x) => x.id));
                const known = new Set(custom.sections.map((x) => x.id));
                const added = c.sections.filter((x) => !known.has(x.id)).map((x) => x.id);
                return { ...s, enabledModules: [...s.enabledModules.filter((id) => !id.startsWith("c-") || ids.has(id)), ...added] };
              });
            }}
          />
        )}

        {tab === "people" && (
          <PeopleTab
            mandatory={settings.mandatory}
            people={people}
            optional={optional}
            custom={custom.sections}
            defaults={settings.enabledModules}
            onChange={(p) => {
              setPeople(p);
              setPeopleDirty(true);
            }}
          />
        )}

        <p className="trn-nomedia">{PICTO_NOTE}</p>
        <Message>{error}</Message>
        <button className="app-btn" disabled={saving} onClick={() => void save()}>
          {saving ? "Wird gespeichert …" : "Speichern"}
        </button>
      </div>
    </Sheet>
  );
}

/* ---------- Eigene Inhalte ---------- */

function OwnContent({ catalog, custom, onChange }: { catalog: CatalogSection[]; custom: TrainingCustom; onChange: (c: TrainingCustom) => void }) {
  const setSection = (i: number, patch: Partial<CustomSection>) => onChange({ ...custom, sections: custom.sections.map((s, k) => (k === i ? { ...s, ...patch } : s)) });
  const extraFor = (id: string) => custom.questions[id] ?? [];
  const setExtra = (id: string, list: TrainingQuestion[]) => {
    const next = { ...custom.questions, [id]: list };
    if (!list.length) delete next[id];
    onChange({ ...custom, questions: next });
  };
  return (
    <>
      <p className="muted-s">
        Eigene Laufwege, Besonderheiten Ihres Betriebs, eigene Regeln: Schreiben Sie sie als eigenen Abschnitt mit Folien und Fragen. Die festen Pflichtinhalte bleiben dabei unverändert.
        Richtige Antworten sehen nur Sie, nie die Teilnehmer.
      </p>
      <h4 className="trn-h4">Eigene Abschnitte</h4>
      {custom.sections.map((s, i) => (
        <div className="trn-own" key={s.id}>
          <label>
            Titel des Abschnitts
            <input value={s.title} maxLength={100} placeholder="z. B. Unser Betriebsgelände" onChange={(e) => setSection(i, { title: e.target.value })} />
          </label>
          <label>
            Kurzbeschreibung
            <input value={s.summary} maxLength={200} placeholder="z. B. Laufwege, Parkplätze, Besonderheiten" onChange={(e) => setSection(i, { summary: e.target.value })} />
          </label>
          <h4>Folien</h4>
          {s.slides.map((sl, k) => (
            <SlideEditor
              key={k}
              slide={sl}
              onChange={(n) => setSection(i, { slides: s.slides.map((x, j) => (j === k ? n : x)) })}
              onRemove={s.slides.length > 1 ? () => setSection(i, { slides: s.slides.filter((_, j) => j !== k) }) : undefined}
            />
          ))}
          {s.slides.length < CUSTOM_LIMITS.slides && (
            <button type="button" className="btn-text" onClick={() => setSection(i, { slides: [...s.slides, newSlide()] })}>
              <Icon name="plus" /> Folie hinzufügen
            </button>
          )}
          <h4>Fragen</h4>
          <QuestionList list={s.questions} max={CUSTOM_LIMITS.questionsPerSection} onChange={(list) => setSection(i, { questions: list })} />
          <button type="button" className="btn-text danger" onClick={() => onChange({ ...custom, sections: custom.sections.filter((_, k) => k !== i) })}>
            Diesen Abschnitt löschen
          </button>
        </div>
      ))}
      {custom.sections.length < CUSTOM_LIMITS.sections && (
        <button
          type="button"
          className="dbtn ghost"
          onClick={() => onChange({ ...custom, sections: [...custom.sections, { id: rid("c-"), title: "", summary: "", slides: [newSlide()], questions: [newQuestion()] }] })}
        >
          <Icon name="plus" /> Eigenen Abschnitt anlegen
        </button>
      )}
      <h4 className="trn-h4">Eigene Fragen zu den festen Abschnitten</h4>
      <p className="muted-s">Zum Beispiel: „Wo ist bei uns der Sammelplatz?“ Die Frage erscheint am Ende des festen Abschnitts und muss richtig beantwortet werden.</p>
      {catalog.map((s) => (
        <details className="trn-own" key={s.id} open={extraFor(s.id).length > 0}>
          <summary>
            {s.title}
            {extraFor(s.id).length ? ` · ${extraFor(s.id).length} eigene` : ""}
          </summary>
          <QuestionList list={extraFor(s.id)} max={CUSTOM_LIMITS.extraQuestions} onChange={(list) => setExtra(s.id, list)} />
        </details>
      ))}
    </>
  );
}

function SlideEditor({ slide, onChange, onRemove }: { slide: TrainingSlide; onChange: (s: TrainingSlide) => void; onRemove?: () => void }) {
  const pictos = slide.pictos ?? [];
  return (
    <div className="trn-q-edit">
      <label>
        Überschrift
        <input value={slide.title} maxLength={120} onChange={(e) => onChange({ ...slide, title: e.target.value })} />
      </label>
      <label>
        Text
        <textarea rows={2} maxLength={800} value={slide.text} onChange={(e) => onChange({ ...slide, text: e.target.value })} />
      </label>
      <label>
        Vero
        <select value={slide.scene ?? "point"} onChange={(e) => onChange({ ...slide, scene: e.target.value as VeroScene })}>
          {SCENES.map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <details>
        <summary>Sicherheitszeichen zeigen ({pictos.length} gewählt, höchstens 4)</summary>
        <div className="trn-pickrow">
          {Object.keys(PICTOS).map((id) => (
            <button
              key={id}
              type="button"
              title={PICTOS[id]!.label}
              className={pictos.includes(id) ? "on" : undefined}
              onClick={() => onChange({ ...slide, pictos: pictos.includes(id) ? pictos.filter((x) => x !== id) : pictos.length < 4 ? [...pictos, id] : pictos })}
            >
              <Picto id={id} size={38} label={false} />
            </button>
          ))}
        </div>
      </details>
      {onRemove && (
        <button type="button" className="btn-text danger" onClick={onRemove}>
          Folie entfernen
        </button>
      )}
    </div>
  );
}

function QuestionList({ list, max, onChange }: { list: TrainingQuestion[]; max: number; onChange: (l: TrainingQuestion[]) => void }) {
  const set = (i: number, q: TrainingQuestion) => onChange(list.map((x, k) => (k === i ? q : x)));
  return (
    <>
      {list.map((q, i) => (
        <div className="trn-q-edit" key={q.id}>
          <label>
            Frage {i + 1}
            <input value={q.text} maxLength={300} placeholder="z. B. Wo ist unser Sammelplatz?" onChange={(e) => set(i, { ...q, text: e.target.value })} />
          </label>
          <small className="muted-s">Häkchen = richtige Antwort. Mehrere Häkchen: Mehrfachauswahl.</small>
          {q.options.map((o, k) => (
            <div className="row" key={k}>
              <input
                type="checkbox"
                aria-label={`Antwort ${k + 1} ist richtig`}
                checked={q.correct.includes(k)}
                onChange={() => set(i, { ...q, correct: q.correct.includes(k) ? q.correct.filter((x) => x !== k) : [...q.correct, k].sort() })}
              />
              <input value={o} maxLength={200} placeholder={`Antwort ${k + 1}`} onChange={(e) => set(i, { ...q, options: q.options.map((x, j) => (j === k ? e.target.value : x)) })} />
              {q.options.length > 2 && (
                <button
                  type="button"
                  className="btn-text danger"
                  onClick={() =>
                    set(i, {
                      ...q,
                      options: q.options.filter((_, j) => j !== k),
                      correct: q.correct.filter((x) => x !== k).map((x) => (x > k ? x - 1 : x)),
                    })
                  }
                >
                  Entfernen
                </button>
              )}
            </div>
          ))}
          {q.options.length < CUSTOM_LIMITS.options && (
            <button type="button" className="btn-text" onClick={() => set(i, { ...q, options: [...q.options, ""] })}>
              <Icon name="plus" /> Antwort hinzufügen
            </button>
          )}
          <label>
            Sicherheitszeichen über der Frage (nicht nötig)
            <select value={q.picto ?? ""} onChange={(e) => set(i, { ...q, picto: e.target.value || undefined })}>
              <option value="">keins</option>
              {Object.entries(PICTOS).map(([id, p]) => (
                <option key={id} value={id}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="btn-text danger" onClick={() => onChange(list.filter((_, k) => k !== i))}>
            Frage löschen
          </button>
        </div>
      ))}
      {list.length < max && (
        <button type="button" className="btn-text" onClick={() => onChange([...list, newQuestion()])}>
          <Icon name="plus" /> Frage hinzufügen
        </button>
      )}
    </>
  );
}

/* ---------- Personen ---------- */

function PeopleTab(props: {
  mandatory: "all" | "selected";
  people: TrainingAssignment[] | null;
  optional: CatalogSection[];
  custom: CustomSection[];
  defaults: string[];
  onChange: (p: TrainingAssignment[]) => void;
}) {
  if (!props.people) return <p className="muted-s">Wird geladen …</p>;
  const all = [...props.optional.map((s) => ({ id: s.id, title: s.title })), ...props.custom.map((s) => ({ id: s.id, title: s.title || "Eigener Abschnitt" }))];
  const set = (userId: string, patch: Partial<TrainingAssignment>) => props.onChange(props.people!.map((p) => (p.userId === userId ? { ...p, ...patch } : p)));
  return (
    <>
      <p className="muted-s">
        {props.mandatory === "all" ? "Alle müssen die Schulung machen. Nehmen Sie das Häkchen weg, wo sie nicht nötig ist." : "Setzen Sie ein Häkchen bei allen, die die Schulung machen müssen."} Außerdem
        können Sie je Person eigene Module wählen. Nicht jeder braucht jedes Modul, zum Beispiel keine Elektrik.
      </p>
      <div className="trn-people">
        {props.people.map((p) => (
          <div className="trn-person" key={p.userId}>
            <label className="trn-confirm" style={{ padding: "8px 12px" }}>
              <input type="checkbox" checked={p.required} onChange={(e) => set(p.userId, { required: e.target.checked })} />
              <span>
                <b>{p.name}</b> muss die Schulung machen
              </span>
            </label>
            <label className="muted-s" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
              <input type="checkbox" checked={p.modules !== null} onChange={(e) => set(p.userId, { modules: e.target.checked ? [...props.defaults] : null })} />
              Eigene Module
            </label>
            {p.modules !== null && (
              <div className="mods">
                {all.map((m) => (
                  <label key={m.id} className={p.modules!.includes(m.id) ? "on" : undefined}>
                    <input
                      type="checkbox"
                      checked={p.modules!.includes(m.id)}
                      onChange={() => set(p.userId, { modules: p.modules!.includes(m.id) ? p.modules!.filter((x) => x !== m.id) : [...p.modules!, m.id] })}
                    />
                    {m.title}
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
